import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import TabNavigation from './TabNavigation';

const AuthenticatedHeader = ({ userRole = 'client', userName = 'User', className = '' }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    
    try {
      setIsLoggingOut(true);
      setIsUserMenuOpen(false);
      
      // Clear localStorage FIRST to ensure immediate state cleanup
      localStorage.removeItem('userRole');
      localStorage.removeItem('userName');
      localStorage.removeItem('authToken');
      localStorage.removeItem('rememberMe');
      sessionStorage.clear();
      
      // Then call Supabase signOut
      await signOut();
      
      // Force immediate navigation regardless of Supabase response
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Error during logout:', error);
      // Force navigation even if error occurs
      window.location.href = '/login';
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isAdminRoute = location?.pathname?.startsWith('/admin');

  return (
    <header className={`sticky top-0 bg-card border-b border-border z-100 ${className}`}>
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Logo Section */}
        <div className="flex items-center space-x-4">
          <Link to="/today" className="flex items-center space-x-2 group">
            <img 
              src="/assets/images/aletheos_MD_horizontal_navy-1758759355229.png" 
              alt="Aletheos MD Logo" 
              className="h-10 w-auto md:h-12 lg:h-14 group-hover:scale-105 transition-transform duration-200"
            />
          </Link>
        </div>

        {/* Desktop Navigation - Only show for client routes */}
        {!isAdminRoute && (
          <div className="hidden lg:flex items-center justify-center flex-1 max-w-md mx-8">
            <TabNavigation />
          </div>
        )}

        {/* Admin Route Title */}
        {isAdminRoute && (
          <div className="hidden lg:block flex-1 text-center">
            <h2 className="text-lg font-heading font-medium text-foreground">
              Admin Dashboard
            </h2>
          </div>
        )}

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-muted transition-all duration-200 animate-spring"
          >
            <div className="flex items-center justify-center w-8 h-8 bg-secondary rounded-full">
              <span className="text-sm font-heading font-medium text-secondary-foreground">
                {userName?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-body font-medium text-foreground">{userName}</p>
              <p className="text-xs text-muted-foreground capitalize">{userRole}</p>
            </div>
            <Icon 
              name="ChevronDown" 
              size={16} 
              className={`text-muted-foreground transition-transform duration-200 ${
                isUserMenuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* User Dropdown Menu */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-gentle-hover animate-gentle-fade">
              <div className="py-2">
                {userRole === 'admin' && !isAdminRoute && (
                  <Link
                    to="/admin-dashboard"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-200"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <Icon name="Settings" size={16} />
                    <span>Admin Dashboard</span>
                  </Link>
                )}
                {userRole === 'admin' && isAdminRoute && (
                  <Link
                    to="/today"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-200"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <Icon name="User" size={16} />
                    <span>Client View</span>
                  </Link>
                )}
                <div className="border-t border-border my-1"></div>
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name={isLoggingOut ? "Loader" : "LogOut"} size={16} className={isLoggingOut ? "animate-spin" : ""} />
                  <span>{isLoggingOut ? 'Signing Out...' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Mobile Navigation Overlay */}
      {isUserMenuOpen && (
        <div 
          className="fixed inset-0 z-50 lg:hidden" 
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default AuthenticatedHeader;